/**
 * DTX — shared utilities for Detox custom sections.
 */
(function () {
  if (window.DTX?.onSection) return;

  const DTX = {
    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },

    /**
     * @param {string} selector
     * @param {(root: HTMLElement) => (() => void) | void} init
     */
    onSection(selector, init) {
      const instances = new WeakMap();

      const mount = (root) => {
        if (!(root instanceof HTMLElement)) return;
        if (instances.has(root)) return;
        const destroy = init(root);
        if (typeof destroy === 'function') instances.set(root, destroy);
      };

      const unmount = (root) => {
        const destroy = instances.get(root);
        if (typeof destroy === 'function') destroy();
        instances.delete(root);
      };

      const mountAll = (scope = document) => {
        scope.querySelectorAll(selector).forEach(mount);
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => mountAll());
      } else {
        mountAll();
      }

      document.addEventListener('shopify:section:load', (event) => {
        const container = event.target;
        if (!(container instanceof HTMLElement)) return;
        container.querySelectorAll(selector).forEach(mount);
      });

      document.addEventListener('shopify:section:unload', (event) => {
        const container = event.target;
        if (!(container instanceof HTMLElement)) return;
        container.querySelectorAll(selector).forEach(unmount);
      });
    },

    /**
     * Reveal elements when they enter the viewport.
     * @param {HTMLElement} root
     * @param {Element[]} targets
     * @param {{ stagger?: number }} [options]
     * @returns {() => void}
     */
    revealOnScroll(root, targets, options = {}) {
      const stagger = options.stagger ?? 0.1;
      const reducedMotion = DTX.prefersReducedMotion();

      if (reducedMotion || targets.length === 0) {
        root.classList.add('is-visible');
        return () => {};
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            root.classList.add('is-visible');
            targets.forEach((target, index) => {
              if (target instanceof HTMLElement) {
                target.style.transitionDelay = `${index * stagger}s`;
              }
            });
            observer.disconnect();
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -12% 0px' }
      );

      observer.observe(root);

      return () => observer.disconnect();
    },

    /**
     * Lightweight horizontal carousel via native overflow scroll (no Swiper).
     * @param {{
     *   track: HTMLElement,
     *   prevBtn?: HTMLElement | null,
     *   nextBtn?: HTMLElement | null,
     *   getStep?: () => number,
     * }} options
     * @returns {() => void}
     */
    initScrollCarousel(options) {
      const { track, prevBtn, nextBtn } = options;
      if (!track) return () => {};

      const getStep =
        options.getStep ||
        (() => {
          const slide = track.querySelector(':scope > *');
          if (!(slide instanceof HTMLElement)) return track.clientWidth * 0.8;
          const styles = getComputedStyle(track);
          const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
          return slide.offsetWidth + gap;
        });

      const updateArrows = () => {
        if (!prevBtn || !nextBtn) return;
        const maxScroll = track.scrollWidth - track.clientWidth - 2;
        prevBtn.disabled = track.scrollLeft <= 2;
        nextBtn.disabled = track.scrollLeft >= maxScroll;
      };

      const onPrev = () => {
        track.scrollBy({ left: -getStep(), behavior: DTX.prefersReducedMotion() ? 'auto' : 'smooth' });
      };
      const onNext = () => {
        track.scrollBy({ left: getStep(), behavior: DTX.prefersReducedMotion() ? 'auto' : 'smooth' });
      };

      prevBtn?.addEventListener('click', onPrev);
      nextBtn?.addEventListener('click', onNext);
      track.addEventListener('scroll', updateArrows, { passive: true });

      let resizeTimer = 0;
      const onResize = () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(updateArrows, 100);
      };
      window.addEventListener('resize', onResize, { passive: true });

      updateArrows();

      return () => {
        prevBtn?.removeEventListener('click', onPrev);
        nextBtn?.removeEventListener('click', onNext);
        track.removeEventListener('scroll', updateArrows);
        window.removeEventListener('resize', onResize);
        window.clearTimeout(resizeTimer);
      };
    },

    /**
     * Register a section initializer after DTX core is available.
     * Safe for lazy-loaded section scripts.
     * @param {string} selector
     * @param {(root: HTMLElement) => (() => void) | void} init
     */
    bootSection(selector, init) {
      const attach = () => {
        if (!DTX.onSection) return false;
        DTX.onSection(selector, init);
        return true;
      };

      if (attach()) return;

      const wait = () => {
        if (attach()) return;
        requestAnimationFrame(wait);
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', wait, { once: true });
      } else {
        wait();
      }
    },
  };

  window.DTX = DTX;
})();
